import { TCreateIndustryReqDto, TCreateIndustryResDto } from "@/api/services/admin/learning-content/dtos/create-industry.dto";
import { TCreateSubjectReqDto, TCreateSubjectResDto } from "@/api/services/admin/learning-content/dtos/create-subject.dto";
import { TCreateExamReqDto, TCreateExamResDto } from "@/api/services/admin/learning-content/dtos/create-exam.dto";
import { TUpdateIndustryReqDto, TUpdateIndustryResDto } from "@/api/services/admin/learning-content/dtos/update-industry.dto";
import { TGetIndustryResDto } from "@/api/services/admin/learning-content/dtos/get-industry.dto";
import { TGetIndustryListReqDto, TGetIndustryListResDto } from "@/api/services/admin/learning-content/dtos/get-industry-list.dto";
import { TDeleteIndustryResDto } from "@/api/services/admin/learning-content/dtos/delete-industry.dto";
import { TUpdateIndustryOrderReqDto, TUpdateIndustryOrderResDto } from "@/api/services/admin/learning-content/dtos/update-industry-order.dto";
import { TUpdateSubjectOrderReqDto, TUpdateSubjectOrderResDto } from "@/api/services/admin/learning-content/dtos/update-subject-order.dto";
import { TGetSubjectListReqDto, TGetSubjectListResDto } from "@/api/services/admin/learning-content/dtos/get-subject-list.dto";
import { TGetExamListReqDto, TGetExamListResDto } from "@/api/services/admin/learning-content/dtos/get-exam-list.dto";
import { TGetExamDetailResDto } from "@/api/services/admin/learning-content/dtos/get-exam-detail.dto";
import { TGetSubjectDetailResDto } from "@/api/services/admin/learning-content/dtos/get-subject-detail.dto";
import { TUpdateExamReqDto, TUpdateExamResDto } from "@/api/services/admin/learning-content/dtos/update-exam.dto";
import { TUpdateSubjectReqDto, TUpdateSubjectResDto } from "@/api/services/admin/learning-content/dtos/update-subject.dto";
import { TGetVocabularyListReqDto, TGetVocabularyListResDto } from "@/api/services/admin/learning-content/dtos/get-vocabulary-list.dto";
import { TGetQuestionListReqDto, TGetQuestionListResDto } from "@/api/services/admin/learning-content/dtos/get-question-list.dto";
import { TImportVocabulariesResDto } from "@/api/services/admin/learning-content/dtos/import-vocabularies.dto";
import { TImportQuestionsResDto } from "@/api/services/admin/learning-content/dtos/import-questions.dto";
import { TCreateVocabularyBookReqDto, TCreateVocabularyBookResDto } from "@/api/services/admin/learning-content/dtos/create-vocabulary-book.dto";
import { TUpdateVocabularyBookReqDto, TUpdateVocabularyBookResDto } from "@/api/services/admin/learning-content/dtos/update-vocabulary-book.dto";
import { TGetVocabularyBookResDto } from "@/api/services/admin/learning-content/dtos/get-vocabulary-book.dto";
import { TGetMockExamResDto, TGetMockExamListResDto } from "@/api/services/admin/learning-content/dtos/get-mock-exam.dto";
import { TUpdateMockExamReqDto, TUpdateMockExamResDto } from "@/api/services/admin/learning-content/dtos/update-mock-exam.dto";
import { TCreateMockExamReqDto, TCreateMockExamResDto } from "@/api/services/admin/learning-content/dtos/create-mock-exam.dto";
import { TDeleteMockExamResDto } from "@/api/services/admin/learning-content/dtos/delete-mock-exam.dto";
import { TGetMockExamQuestionListResDto } from "@/api/services/admin/learning-content/dtos/get-mock-exam-question-list.dto";
import { TImportMockExamResDto } from "@/api/services/admin/learning-content/dtos/sync-mock-exam.dto";
import { TSyncIndustryResDto } from "@/api/services/admin/learning-content/dtos/sync-industry.dto";
import { TPublishIndustryResDto } from "@/api/services/admin/learning-content/dtos/publish-industry.dto";
import { TUnpublishIndustryResDto } from "@/api/services/admin/learning-content/dtos/unpublish-industry.dto";
import { clientApi } from "@/utils/api";
import { TApiRes } from "@/types/api";

/**
 * Create a new industry
 */
export const createIndustryApi = async (payload: TCreateIndustryReqDto): Promise<TCreateIndustryResDto> => {
  const rs = await clientApi.post<TCreateIndustryResDto>("/learning-content/industries", payload);
  return rs.data;
};

/**
 * Get paginated list of industries
 */
export const getIndustryListApi = async (params?: TGetIndustryListReqDto): Promise<TGetIndustryListResDto> => {
  const rs = await clientApi.get<TGetIndustryListResDto>("/learning-content/industries", { params });
  return rs.data;
};

/**
 * Get industry detail by ID
 */
export const getIndustryApi = async (industryId: string): Promise<TGetIndustryResDto> => {
  const rs = await clientApi.get<TGetIndustryResDto>(`/learning-content/industries/${industryId}`);
  return rs.data;
};

/**
 * Update an existing industry
 */
export const updateIndustryApi = async (industryId: string, payload: TUpdateIndustryReqDto): Promise<TUpdateIndustryResDto> => {
  const rs = await clientApi.put<TUpdateIndustryResDto>(`/learning-content/industries/${industryId}`, payload);
  return rs.data;
};

/**
 * Delete an industry (soft delete)
 */
export const deleteIndustryApi = async (industryId: string): Promise<TDeleteIndustryResDto> => {
  const rs = await clientApi.delete<TDeleteIndustryResDto>(`/learning-content/industries/${industryId}`);
  return rs.data;
};

/**
 * Sync industry content and publish changes
 */
export const syncIndustryApi = async (industryId: string): Promise<TSyncIndustryResDto> => {
  const rs = await clientApi.post<TSyncIndustryResDto>(`/learning-content/industries/${industryId}/sync`);
  return rs.data;
};

/**
 * Publish industry
 */
export const publishIndustryApi = async (industryId: string): Promise<TPublishIndustryResDto> => {
  const rs = await clientApi.patch<TPublishIndustryResDto>(`/learning-content/industries/${industryId}/publish`);
  return rs.data;
};

/**
 * Unpublish industry
 */
export const unpublishIndustryApi = async (industryId: string): Promise<TUnpublishIndustryResDto> => {
  const rs = await clientApi.patch<TUnpublishIndustryResDto>(`/learning-content/industries/${industryId}/unpublish`);
  return rs.data;
};

/**
 * Update industry display order
 */
export const updateIndustryOrderApi = async (payload: TUpdateIndustryOrderReqDto): Promise<TUpdateIndustryOrderResDto> => {
  const rs = await clientApi.put<TUpdateIndustryOrderResDto>("/learning-content/industries/order", payload);
  return rs.data;
};

/**
 * Update subject display order within an industry
 */
export const updateSubjectOrderApi = async (industryId: string, payload: TUpdateSubjectOrderReqDto): Promise<TUpdateSubjectOrderResDto> => {
  const rs = await clientApi.put<TUpdateSubjectOrderResDto>(`/learning-content/industries/${industryId}/subjects/order`, payload);
  return rs.data;
};

/**
 * Create a new subject
 */
export const createSubjectApi = async (payload: TCreateSubjectReqDto): Promise<TCreateSubjectResDto> => {
  const rs = await clientApi.post<TCreateSubjectResDto>("/learning-content/subjects", payload);
  return rs.data;
};

/**
 * Update an existing subject
 */
export const updateSubjectApi = async (subjectId: string, payload: TUpdateSubjectReqDto): Promise<TUpdateSubjectResDto> => {
  const rs = await clientApi.put<TUpdateSubjectResDto>(`/learning-content/subjects/${subjectId}`, payload);
  return rs.data;
};

/**
 * Toggle subject active status
 */
export const toggleSubjectStatusApi = async (subjectId: string, payload: { isActive: boolean }): Promise<TUpdateSubjectResDto> => {
  const rs = await clientApi.patch<TUpdateSubjectResDto>(`/learning-content/subjects/${subjectId}/toggle-status`, payload);
  return rs.data;
};

/**
 * Get paginated list of subjects for an industry
 */
export const getSubjectListApi = async (params: TGetSubjectListReqDto): Promise<TGetSubjectListResDto> => {
  const rs = await clientApi.get<TGetSubjectListResDto>("/learning-content/subjects", { params });
  return rs.data;
};

/**
 * Get subject detail by ID
 */
export const getSubjectDetailApi = async (subjectId: string): Promise<TGetSubjectDetailResDto> => {
  const rs = await clientApi.get<TGetSubjectDetailResDto>(`/learning-content/subjects/${subjectId}`);
  return rs.data;
};

/**
 * Get paginated list of exams for an industry
 */
export const getExamListApi = async (params: TGetExamListReqDto): Promise<TGetExamListResDto> => {
  const rs = await clientApi.get<TGetExamListResDto>("/learning-content/exams", { params });
  return rs.data;
};

/**
 * Create a new exam info under an industry
 */
export const createExamApi = async (payload: TCreateExamReqDto): Promise<TCreateExamResDto> => {
  const rs = await clientApi.post<TCreateExamResDto>("/learning-content/exams", payload);
  return rs.data;
};

/**
 * Get exam detail by ID
 */
export const getExamDetailApi = async (examId: string): Promise<TGetExamDetailResDto> => {
  const rs = await clientApi.get<TGetExamDetailResDto>(`/learning-content/exams/${examId}`);
  return rs.data;
};

/**
 * Update an existing exam
 */
export const updateExamApi = async (examId: string, payload: TUpdateExamReqDto): Promise<TUpdateExamResDto> => {
  const rs = await clientApi.put<TUpdateExamResDto>(`/learning-content/exams/${examId}`, payload);
  return rs.data;
};

/**
 * Get paginated list of vocabularies for a subject
 */
export const getVocabularyListApi = async (subjectId: string, params?: TGetVocabularyListReqDto): Promise<TGetVocabularyListResDto> => {
  const rs = await clientApi.get<TGetVocabularyListResDto>(`/learning-content/subjects/${subjectId}/vocabularies`, { params });
  return rs.data;
};

/**
 * Import vocabularies for a subject from spreadsheet
 */
export const importVocabulariesApi = async (subjectId: string): Promise<TImportVocabulariesResDto> => {
  const rs = await clientApi.post<TImportVocabulariesResDto>(`/learning-content/subjects/${subjectId}/import-vocabularies`);
  return rs.data;
};

/**
 * Get paginated list of questions for a subject
 */
export const getQuestionListApi = async (subjectId: string, params?: TGetQuestionListReqDto): Promise<TGetQuestionListResDto> => {
  const rs = await clientApi.get<TGetQuestionListResDto>(`/learning-content/subjects/${subjectId}/questions`, { params });
  return rs.data;
};

/**
 * Import questions for a subject from spreadsheet
 */
export const importQuestionsApi = async (subjectId: string): Promise<TImportQuestionsResDto> => {
  const rs = await clientApi.post<TImportQuestionsResDto>(`/learning-content/subjects/${subjectId}/import-questions`);
  return rs.data;
};

/**
 * Get vocabulary book for an industry
 */
export const getVocabularyBookApi = async (industryId: string): Promise<TGetVocabularyBookResDto> => {
  const rs = await clientApi.get<TGetVocabularyBookResDto>(`/learning-content/industries/${industryId}/vocabulary-book`);
  return rs.data;
};

/**
 * Create vocabulary book for an industry
 */
export const createVocabularyBookApi = async (industryId: string, payload: TCreateVocabularyBookReqDto): Promise<TCreateVocabularyBookResDto> => {
  const rs = await clientApi.post<TCreateVocabularyBookResDto>(`/learning-content/industries/${industryId}/vocabulary-book`, payload);
  return rs.data;
};

/**
 * Update vocabulary book for an industry
 */
export const updateVocabularyBookApi = async (industryId: string, payload: TUpdateVocabularyBookReqDto): Promise<TUpdateVocabularyBookResDto> => {
  const rs = await clientApi.put<TUpdateVocabularyBookResDto>(`/learning-content/industries/${industryId}/vocabulary-book`, payload);
  return rs.data;
};

/**
 * Get paginated vocabulary book list for an industry
 */
export const getVocabularyBookListApi = async (industryId: string, params?: TGetVocabularyListReqDto): Promise<TGetVocabularyListResDto> => {
  const rs = await clientApi.get<TGetVocabularyListResDto>(`/learning-content/industries/${industryId}/vocabulary-book/vocabularies`, { params });
  return rs.data;
};

/**
 * Import vocabulary book data for an industry from spreadsheet
 */
export const importVocabularyBookApi = async (industryId: string): Promise<TImportVocabulariesResDto> => {
  const rs = await clientApi.post<TImportVocabulariesResDto>(`/learning-content/industries/${industryId}/import-vocabulary-book`);
  return rs.data;
};

/**
 * Get the list of mock exams for an industry
 */
export const getMockExamListApi = async (industryId: string): Promise<TGetMockExamListResDto> => {
  const rs = await clientApi.get<TGetMockExamListResDto>(`/learning-content/industries/${industryId}/mock-exams`);
  return rs.data;
};

/**
 * Create a new mock exam for an industry
 */
export const createMockExamApi = async (industryId: string, payload: TCreateMockExamReqDto): Promise<TCreateMockExamResDto> => {
  const rs = await clientApi.post<TCreateMockExamResDto>(`/learning-content/industries/${industryId}/mock-exams`, payload);
  return rs.data;
};

/**
 * Toggle a mock exam's active (有効) status. Used only from the list screen.
 */
export const toggleMockExamStatusApi = async (
  industryId: string,
  mockExamId: string,
  payload: { isActive: boolean },
): Promise<TApiRes<{ id: number; isActive: boolean }>> => {
  const rs = await clientApi.patch<TApiRes<{ id: number; isActive: boolean }>>(
    `/learning-content/industries/${industryId}/mock-exams/${mockExamId}/toggle-status`,
    payload,
  );
  return rs.data;
};

/**
 * Update the display order of mock exams within an industry
 */
export const reorderMockExamsApi = async (industryId: string, payload: { mockExamIds: number[] }): Promise<TApiRes<{ industryId: number }>> => {
  const rs = await clientApi.put<TApiRes<{ industryId: number }>>(`/learning-content/industries/${industryId}/mock-exams/order`, payload);
  return rs.data;
};

/**
 * Get a single mock exam by id
 */
export const getMockExamApi = async (industryId: string, mockExamId: string): Promise<TGetMockExamResDto> => {
  const rs = await clientApi.get<TGetMockExamResDto>(`/learning-content/industries/${industryId}/mock-exams/${mockExamId}`);
  return rs.data;
};

/**
 * Update a mock exam by id
 */
export const updateMockExamApi = async (industryId: string, mockExamId: string, payload: TUpdateMockExamReqDto): Promise<TUpdateMockExamResDto> => {
  const rs = await clientApi.put<TUpdateMockExamResDto>(`/learning-content/industries/${industryId}/mock-exams/${mockExamId}`, payload);
  return rs.data;
};

/**
 * Delete a mock exam by id
 */
export const deleteMockExamApi = async (industryId: string, mockExamId: string): Promise<TDeleteMockExamResDto> => {
  const rs = await clientApi.delete<TDeleteMockExamResDto>(`/learning-content/industries/${industryId}/mock-exams/${mockExamId}`);
  return rs.data;
};

/**
 * Get paginated mock exam question list for a mock exam
 */
export const getMockExamQuestionListApi = async (
  industryId: string,
  mockExamId: string,
  params?: { page?: number; perPage?: number; sorts?: string[] },
): Promise<TGetMockExamQuestionListResDto> => {
  const rs = await clientApi.get<TGetMockExamQuestionListResDto>(`/learning-content/industries/${industryId}/mock-exams/${mockExamId}/questions`, {
    params,
  });
  return rs.data;
};

/**
 * Sync mock exam data from spreadsheet
 */
export const syncMockExamApi = async (industryId: string, mockExamId: string): Promise<TImportMockExamResDto> => {
  const rs = await clientApi.post<TImportMockExamResDto>(`/learning-content/industries/${industryId}/mock-exams/${mockExamId}/sync`);
  return rs.data;
};
